import { digestPinnedImageRefSchema } from "./desired-state.js";
import { RIGHTS_CERTIFICATE_MAX_BYTES, sha256DigestSchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/rights-certificate-wire.ts
/**
* The rights certificate on the CP to DP wire (2026-10-03-kernel-apps, WS-2a, R-005, R-023).
*
* The CP signs one rights certificate per app version (WS-8b) and puts the compact JWS on
* `AppDesiredState.rightsCertificate` and on each `FixtureVersionedDesiredState.rightsCertificate`.
* The operator program (WS-22) and core-api (WS-21a) verify it against the pinned CP key with the
* `@zion/sdk-contracts` verifier (`createRightsCertificateVerifier`).
*
* This module bounds the carrier and never decodes it. The verifier owns the signature, `typ`,
* `kid` and the claims, so the trust decision stays in one function. This module also owns
* {@link imageDigestOf}, the one parse of a digest out of a digest-pinned image reference, which
* the operator program calls before each `rightsCertificateCoversImage` lookup.
*
* Browser-safe: zod and `@zion/sdk-contracts` only, no Node builtin.
*/
/**
* The longest rights certificate on the wire, in characters: 65,536.
*
* R-005 sets one size budget, so this is the `@zion/sdk-contracts` constant
* `RIGHTS_CERTIFICATE_MAX_BYTES` and never a second literal. The wire grammar
* ({@link COMPACT_JWS_PATTERN}) admits ASCII only, so a character is a byte here.
*/
const RIGHTS_CERTIFICATE_WIRE_MAX_LENGTH = RIGHTS_CERTIFICATE_MAX_BYTES;
/**
* A compact JWS: three non-empty base64url segments that two dots join.
*
* It refuses an empty segment, `+`, `/`, `=`, white space, and two or four segments. No class
* holds the dot, so a match runs in linear time.
*/
const COMPACT_JWS_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
/**
* The bound on the rights certificate carrier. Untrusted input until the verifier passes.
*
* Callers: the CP producer (WS-8b) before it assigns a desired-state member, and the two
* desired-state schemas in this package. Input: any value. Output for each input class:
*
* 1. a string of 1 to 65,536 characters that {@link COMPACT_JWS_PATTERN} matches: the same string;
* 2. a string over 65,536 characters: one `too_big` issue, and the pattern never runs (the length
*    check aborts, the ReDoS order of the repo);
* 3. the empty string: a `too_small` issue and an `invalid_format` issue;
* 4. any other string: an `invalid_format` issue;
* 5. a non-string (`null`, a number, an object): an `invalid_type` issue.
*
* It never decodes a segment, so a payload that is not JSON passes here and fails in the verifier.
* It has no `.transform()`, so `createOpenApiDocument()` can represent it. No side effects.
*/
const rightsCertificateOnWireSchema = z.string().min(1).max(RIGHTS_CERTIFICATE_WIRE_MAX_LENGTH, { abort: true }).regex(COMPACT_JWS_PATTERN);
/** The message of the `TypeError` that {@link imageDigestOf} throws. */
const NOT_DIGEST_PINNED = "imageDigestOf: not a digest-pinned image reference";
/**
* The `sha256:<64 lowercase hex>` digest of a digest-pinned image reference.
*
* Caller: the operator program (WS-22), before each certificate lookup by image digest, on each
* `FixtureVersionedDesiredState.imageRef`. The operator program runs no zod on the envelope, so the
* input is untrusted.
*
* Input: a value that `digestPinnedImageRefSchema` accepts: at most 512 characters, the grammar
* `DIGEST_PINNED_IMAGE_REF_PATTERN`, no registry prefix. Output: the text after the `@`, for
* example `sha256:` and 64 hex characters for `team/notes@sha256:<hex>`.
*
* Errors: every other input throws `TypeError('imageDigestOf: not a digest-pinned image reference')`.
* That covers a tag (`notes:1.0`), a bare repository, a registry prefix with a port, a `sha512`
* digest, upper-case hex, 63 or 65 hex characters, over 512 characters, and a non-string. It never
* returns a partial digest. Pure: no side effects, no clock, no I/O.
*/
function imageDigestOf(imageRef) {
	if (!digestPinnedImageRefSchema.safeParse(imageRef).success) throw new TypeError(NOT_DIGEST_PINNED);
	return sha256DigestSchema.parse(imageRef.slice(imageRef.indexOf("@") + 1));
}
//#endregion
export { COMPACT_JWS_PATTERN, RIGHTS_CERTIFICATE_WIRE_MAX_LENGTH, imageDigestOf, rightsCertificateOnWireSchema };

//# sourceMappingURL=rights-certificate-wire.js.map